import React, { useState, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';

/**
 * Material Design 3 Ripple Effect Component
 * Provides touch-optimized ripple feedback for interactive elements
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child element to attach ripple to
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.color] - Ripple color ('light' | 'dark' | 'brand')
 * @param {boolean} [props.disabled] - Disable ripple effect
 */
export function Ripple({ 
  children, 
  className, 
  color = 'light',
  disabled = false 
}) {
  const [ripples, setRipples] = useState([]);
  const containerRef = useRef(null);
  const rippleIdRef = useRef(0);

  const getRippleColor = () => {
    switch (color) {
      case 'dark':
        return 'rgba(255, 255, 255, 0.3)';
      case 'brand':
        return 'rgba(104, 94, 255, 0.3)';
      case 'light':
      default:
        return 'rgba(0, 0, 0, 0.1)';
    }
  };

  const createRipple = useCallback((event) => {
    if (disabled) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    
    // Get touch point or mouse position
    const clientX = event.touches?.[0]?.clientX || event.clientX;
    const clientY = event.touches?.[0]?.clientY || event.clientY;
    
    // Calculate position relative to container
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    // Calculate size (use diagonal to ensure ripple covers entire element)
    const size = Math.max(rect.width, rect.height);
    const diameter = size * 2;
    
    const newRipple = {
      id: rippleIdRef.current++,
      x: x - size,
      y: y - size,
      size: diameter,
    };

    setRipples((prev) => [...prev, newRipple]);

    // Remove ripple after animation completes
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
    }, 600);
  }, [disabled]);

  const handleMouseDown = useCallback((event) => {
    // Only handle primary mouse button
    if (event.button === 0) {
      createRipple(event);
    }
  }, [createRipple]);

  const handleTouchStart = useCallback((event) => {
    createRipple(event);
  }, [createRipple]);

  // Clone child and add event handlers
  const child = React.Children.only(children);
  const originalChildren = child.props.children;
  
  const childWithProps = React.cloneElement(child, {
    ref: (node) => {
      containerRef.current = node;
      // Preserve existing ref if any
      if (typeof child.ref === 'function') {
        child.ref(node);
      } else if (child.ref) {
        child.ref.current = node;
      }
    },
    onMouseDown: (e) => {
      handleMouseDown(e);
      child.props.onMouseDown?.(e);
    },
    onTouchStart: (e) => {
      handleTouchStart(e);
      child.props.onTouchStart?.(e);
    },
    className: cn('relative overflow-hidden', child.props.className, className),
    children: (
      <>
        {originalChildren}
        {ripples.length > 0 && (
          <span className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg">
            {ripples.map((ripple) => (
              <span
                key={ripple.id}
                className="absolute rounded-full animate-ripple origin-center"
                style={{
                  left: `${ripple.x}px`,
                  top: `${ripple.y}px`,
                  width: `${ripple.size}px`,
                  height: `${ripple.size}px`,
                  backgroundColor: getRippleColor(),
                }}
              />
            ))}
          </span>
        )}
      </>
    ),
  });

  return childWithProps;
}

export default Ripple;

